/**
 * SupplierRFQQuoteBoundary (AJIP-16.02.008 · WS20)
 * ---------------------------------------------------------------------
 * Interface contract for RFQ opportunities and quote submissions
 * received by the Supplier Journey. This module is:
 *   - Validation-only.
 *   - Provider-independent.
 *   - Free of external-system imports (no RFQ page imports, no
 *     analytics dispatch, no data client access).
 *   - Free of storage access.
 *   - Free of customer-facing behaviour.
 *   - Without authority over eligibility, activation, progression or
 *     completion.
 *
 * Callers supply already-authorised references explicitly. Boundary
 * validates shape only.
 */
import { SupplierRFQQuoteBoundaryError } from './SupplierJourneyErrors';

const MAX_LEN = 128;
const REF_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._:@\-/]{0,127}$/;
const isNonEmptyString = (v) => typeof v === 'string' && v.trim().length > 0;

const validateRfqOpportunityRef = (rfqRef) => {
  if (!isNonEmptyString(rfqRef)) throw new SupplierRFQQuoteBoundaryError('rfqRef must be non-empty string');
  if (rfqRef.length > MAX_LEN) throw new SupplierRFQQuoteBoundaryError('rfqRef too long');
  if (!REF_PATTERN.test(rfqRef)) throw new SupplierRFQQuoteBoundaryError('rfqRef has invalid shape');
  return true;
};

const validateQuoteRef = (quoteRef) => {
  if (!isNonEmptyString(quoteRef)) throw new SupplierRFQQuoteBoundaryError('quoteRef must be non-empty string');
  if (quoteRef.length > MAX_LEN) throw new SupplierRFQQuoteBoundaryError('quoteRef too long');
  if (!REF_PATTERN.test(quoteRef)) throw new SupplierRFQQuoteBoundaryError('quoteRef has invalid shape');
  return true;
};

/**
 * Attach an authorised RFQ opportunity reference into supplier context.
 * Boundary never reads storage.
 */
const attachRfqOpportunity = ({ supplierContext, rfqRef }) => {
  validateRfqOpportunityRef(rfqRef);
  return Object.freeze({ ...(supplierContext || {}), rfqOpportunityRef: rfqRef });
};

/**
 * Attach an authorised quote reference into supplier context.
 * Boundary never reads storage.
 */
const attachQuoteReference = ({ supplierContext, quoteRef }) => {
  validateQuoteRef(quoteRef);
  return Object.freeze({ ...(supplierContext || {}), quoteRef });
};

const BOUNDARY_GOVERNANCE = Object.freeze({
  supplier_may: 'record-opaque-rfq-and-quote-references',
  supplier_shall_never: 'submit-authoritative-quotes|read-quote-storage|call-rfq-api',
});

const BOUNDARY_NAME = 'SupplierRFQQuoteBoundary';

export {
  BOUNDARY_NAME,
  BOUNDARY_GOVERNANCE,
  validateRfqOpportunityRef,
  validateQuoteRef,
  attachRfqOpportunity,
  attachQuoteReference,
};
